import { useEffect, useState } from "react";
import { Plus, Trash2 } from "lucide-react";
import { Control, useFieldArray, useWatch } from "react-hook-form";

import { Button } from "@/components/ui/button";
import {
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

import { InvoiceFormValues } from "./types";

/**
 * STA-682: formatted amount input that shows commas on blur (e.g. "10,000")
 * and raw digits while editing. Uses type="text" + inputMode="decimal" so
 * mobile still gets the numeric keyboard.
 */
const AmountInput = ({
  value,
  onChange,
  currency,
  disabled,
}: {
  value: number;
  onChange: (val: number) => void;
  currency: string;
  disabled?: boolean;
}) => {
  const [focused, setFocused] = useState(false);
  const [rawText, setRawText] = useState("");

  const displayValue = focused
    ? rawText
    : value
      ? value.toLocaleString("en-US", {
          minimumFractionDigits: 0,
          maximumFractionDigits: 2,
        })
      : "";

  return (
    <div className="flex items-center h-9 rounded-lg border border-sds-neutral-20 bg-white hover:border-sds-neutral-40 focus-within:border-sds-purple-100 overflow-hidden">
      <span className="shrink-0 pl-4 text-sm text-muted-foreground select-none">
        {currency}
      </span>
      <input
        type="text"
        inputMode="decimal"
        placeholder="0.00"
        className="flex-1 h-full min-w-0 bg-transparent px-2 text-sm outline-none border-none focus:outline-none focus:ring-0 disabled:cursor-not-allowed disabled:text-sds-neutral-60"
        value={displayValue}
        onFocus={() => {
          setFocused(true);
          setRawText(value ? String(value) : "");
        }}
        onBlur={() => {
          setFocused(false);
          const parsed = parseFloat(rawText) || 0;
          onChange(parsed);
        }}
        onChange={(e) => {
          // Allow digits, decimal point, and empty string
          const cleaned = e.target.value.replace(/[^0-9.]/g, "");
          setRawText(cleaned);
          const parsed = parseFloat(cleaned) || 0;
          onChange(parsed);
        }}
        disabled={disabled}
      />
    </div>
  );
};

interface LineItemsEditorProps {
  control: Control<InvoiceFormValues>;
  disabled?: boolean;
  onTotalChange?: (total: number) => void;
}

const LineItemsEditor = ({
  control,
  disabled,
  onTotalChange,
}: LineItemsEditorProps) => {
  const { fields, append, remove } = useFieldArray({
    control,
    name: "lineItems",
  });

  const lineItems = useWatch({ control, name: "lineItems" });

  const currency = useWatch({
    control,
    name: "currency",
    defaultValue: "USD",
  });

  const total = (lineItems || []).reduce(
    (sum, item) => sum + (Number(item?.amount) || 0),
    0,
  );

  useEffect(() => {
    if (onTotalChange) {
      onTotalChange(total);
    }
  }, [total, onTotalChange]);

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currency || "USD",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <Label className="text-sm font-medium">Line Items</Label>
        {/* STA-682: hide the Add Item affordance entirely when disabled
            (read-only finalized invoice, or before a customer is picked on
            a new one) rather than rendering a greyed-out button. */}
        {!disabled && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => append({ description: "", amount: 0 })}
            className="h-7 text-xs gap-1"
          >
            <Plus className="h-3.5 w-3.5" />
            Add Item
          </Button>
        )}
      </div>

      <div className="space-y-3">
        {fields.map((field, index) => (
          <div
            key={field.id}
            className="space-y-2 p-3 rounded-lg border border-[#e8e8e8] bg-[#fafafa]"
          >
            <FormField
              control={control}
              name={`lineItems.${index}.description`}
              render={({ field }) => (
                <FormItem className="space-y-1">
                  <FormControl>
                    <Input
                      placeholder="Description (e.g. UX Audit, Wireframes)"
                      {...field}
                      disabled={disabled}
                      className="h-9 text-sm"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <div className="flex gap-2 items-start">
              <div className="flex-1">
                <FormField
                  control={control}
                  name={`lineItems.${index}.amount`}
                  render={({ field }) => (
                    <FormItem className="space-y-1">
                      <FormControl>
                        <AmountInput
                          value={field.value}
                          onChange={field.onChange}
                          currency={currency || "USD"}
                          disabled={disabled}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              {/* STA-682: same read-only treatment as Add Item — hide the
                  trash button entirely when editing is disabled. */}
              {fields.length > 1 && !disabled && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => remove(index)}
                  className="h-9 w-9 p-0 text-muted-foreground hover:text-destructive shrink-0"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              )}
            </div>
          </div>
        ))}
      </div>

      {fields.length > 1 && (
        <div className="flex justify-end pt-2 border-t border-[#e8e8e8]">
          <div className="text-sm">
            <span className="text-muted-foreground">Total: </span>
            <span className="font-semibold">{formatCurrency(total)}</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default LineItemsEditor;
